import { AlertCircle, Check } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import { cn } from '@/src/utils/styles'
import { ChainImg } from '../../ui/chain-img'
import { ListRowButton, ListRowSide, ListRowSideName } from '../../ui/list-row'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'
import { CopyAddressButton } from '../copy-button'
import type { ChainToken } from './types'

export function NetworkRow({
  chain_id,
  chain_img,
  chain_name,
  chain_type,
  denom,
  display_identifier,
  showTooltips,
  directionalChain,
  directionalDenom,
  isDisabled,
  onSelectNetwork,
}: ChainToken & {
  showTooltips?: boolean
  directionalChain?: string | null
  directionalDenom?: string | null
  onSelectNetwork?: (chainToken: ChainToken) => void
  isDisabled?: boolean
}) {
  return (
    <CopyAddressButton
      key={`${chain_id}-${denom}`}
      enabled={showTooltips && !isDisabled}
      tooltipContent={({ isHolding }) => (
        <TooltipContent
          side='right'
          hideArrow
          className='flex flex-col gap-1 max-w-64'
        >
          <AnimatePresence mode='popLayout'>
            {!isHolding && (
              <motion.span
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                }}
                className='text-xs text-primary font-semibold'
              >
                Token address
              </motion.span>
            )}
          </AnimatePresence>
          <AnimatePresence mode='popLayout'>
            {isHolding && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.3,
                  ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
                }}
                className='text-xs text-success-text font-semibold'
              >
                Copied to clipboard
              </motion.span>
            )}
          </AnimatePresence>
          <span className='text-xs text-secondary break-words'>{denom}</span>
        </TooltipContent>
      )}
      contentToCopy={denom}
      triggerButtonClassName={cn({
        'right-12': directionalChain === chain_id && denom === directionalDenom,
      })}
    >
      <ListRowButton
        className={cn('py-2 peer', {
          'group-hover:bg-surface1': !isDisabled,
        })}
        disabled={isDisabled}
        onClick={() =>
          onSelectNetwork?.({
            chain_id,
            chain_img,
            chain_name,
            chain_type,
            denom,
            display_identifier,
          })
        }
      >
        <ListRowSide className='flex-row items-center gap-3'>
          <ChainImg
            chainId={chain_id}
            chainImg={chain_img}
            chainName={chain_name}
            width={20}
            height={20}
            containerClassName='size-8'
          />
          <ListRowSideName>{chain_name}</ListRowSideName>
        </ListRowSide>
        {!isDisabled && (
          <ListRowSide className='flex-row items-center gap-3'>
            {directionalChain === chain_id && denom === directionalDenom && (
              <Check className='size-6 text-link' />
            )}
          </ListRowSide>
        )}
      </ListRowButton>
      {isDisabled && (
        <Tooltip>
          <TooltipTrigger className='absolute right-4 top-3 opacity-50'>
            <AlertCircle size={24} className='text-danger-text' />
          </TooltipTrigger>
          <TooltipContent
            side='right'
            hideArrow
            className='flex flex-col max-w-64'
          >
            <span className='text-xs font-semibold'>
              {chain_name} is temporarily unavailable
            </span>
            <span className='text-xs text-secondary'>
              Please try again later
            </span>
          </TooltipContent>
        </Tooltip>
      )}
    </CopyAddressButton>
  )
}
